import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import Subscription from "../components/Subscription";
import FAQ from "../components/FAQ";
import CalculatorWidget from "../components/CalculatorWidget";
import Contact from "../components/Contact";
import { housePrices, servicePrices, priceFactors, pricingFaq, PRICE_NOTE } from "../data/pricing";
import { serviceIndex } from "../data/services";

const SITE_URL = "https://www.clevencleaning.nl";
const URL = `${SITE_URL}/kosten-glazenwasser`;

export default function PricingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "FAQPage",
        mainEntity: pricingFaq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Wat kost een glazenwasser?", item: URL },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title="Wat kost een glazenwasser? Prijzen 2026 Rosmalen & Den Bosch | Cleven Cleaning"
        description="Wat kost een glazenwasser in Rosmalen of Den Bosch? Bekijk de vanaf-prijzen per woningtype (rijtjeswoning vanaf €35 per beurt), het abonnementsvoordeel en tarieven voor zonnepanelen, dakgoten en hogedrukreiniging."
        canonicalUrl={URL}
        jsonLd={jsonLd}
      />

      <PageHero
        testId="pricing-hero"
        crumbs={[{ label: "Home", to: "/" }, { label: "Prijzen" }]}
        badge="Prijzen & tarieven 2026"
        title="Wat kost een glazenwasser in Rosmalen en Den Bosch?"
        intro="Voor de buitenzijde van een gemiddelde rijtjeswoning betaalt u bij Cleven Cleaning vanaf €35 per beurt. Bij een vast schema ligt de prijs per beurt lager. Hieronder ziet u de vanaf-prijzen per woningtype en per dienst — en met de calculator weet u in 60 seconden precies waar u aan toe bent."
      />

      {/* Woningtypes */}
      <section className="py-24 bg-white" aria-labelledby="house-prices-heading">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="house-prices-heading" className="text-3xl sm:text-4xl font-bold text-slate-800 mb-3">Prijs glasbewassing per woningtype</h2>
          <p className="text-lg text-slate-600 mb-8">Buitenzijde, inclusief kozijnen afnemen. Vanaf-prijzen per beurt; bij een vast schema betaalt u per beurt minder.</p>
          <div className="overflow-x-auto rounded-3xl border border-slate-200 shadow-lg" data-testid="house-price-table">
            <table className="w-full text-left">
              <thead className="bg-gradient-to-r from-slate-900 via-sky-900 to-teal-900 text-white">
                <tr>
                  <th className="px-5 py-4 font-semibold">Woningtype</th>
                  <th className="px-5 py-4 font-semibold">Vanaf per beurt</th>
                  <th className="px-5 py-4 font-semibold hidden md:table-cell">Toelichting</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {housePrices.map((row) => (
                  <tr key={row.type} className="odd:bg-white even:bg-slate-50">
                    <td className="px-5 py-4 font-semibold text-slate-800">{row.type}</td>
                    <td className="px-5 py-4 text-teal-700 font-bold text-lg" data-testid={`house-price-${row.type.split(" ")[0].toLowerCase()}`}>{row.from}</td>
                    <td className="px-5 py-4 text-slate-500 text-sm hidden md:table-cell">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="flex items-start gap-2 text-sm text-slate-500 mt-4"><Info className="w-4 h-4 mt-0.5 flex-shrink-0" />{PRICE_NOTE}</p>
          <div className="mt-8 text-center">
            <a href="#calculator" className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-sky-500 to-teal-500 text-white font-semibold rounded-xl hover:from-sky-600 hover:to-teal-600 shadow-lg shadow-sky-500/25 transition-all" data-testid="pricing-calculator-cta">
              Bereken uw exacte prijs
            </a>
          </div>
        </div>
      </section>

      {/* Factoren */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-sky-50" aria-labelledby="factors-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="factors-heading" className="text-3xl sm:text-4xl font-bold text-slate-800 mb-12 text-center">Wat bepaalt de prijs?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {priceFactors.map((f, i) => (
              <div key={f.title} className="bg-white rounded-2xl p-6 shadow-md">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-teal-500 text-white font-bold flex items-center justify-center mb-4">{i + 1}</div>
                <h3 className="font-bold text-slate-800 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overige diensten */}
      <section className="py-24 bg-white" aria-labelledby="service-prices-heading">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="service-prices-heading" className="text-3xl sm:text-4xl font-bold text-slate-800 mb-3">Tarieven overige diensten</h2>
          <p className="text-lg text-slate-600 mb-8">Vanaf-prijzen. Klik op een dienst voor meer informatie.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="service-price-list">
            {serviceIndex.map((s) => {
              const p = servicePrices[s.slug];
              return (
                <Link key={s.slug} to={`/${s.slug}`} data-testid={`pricing-service-link-${s.slug}`} className="group flex items-center justify-between gap-4 bg-gradient-to-br from-slate-50 to-sky-50 rounded-2xl p-5 border border-slate-100 hover:border-sky-300 hover:shadow-lg transition-all">
                  <div>
                    <p className="font-semibold text-slate-800 group-hover:text-sky-600 transition-colors">{s.title}</p>
                    <p className="text-xs text-slate-500">{p.note}</p>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    <div>
                      <p className="font-bold text-teal-700">vanaf {p.from}</p>
                      <p className="text-xs text-slate-500">{p.unit}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-sky-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Subscription />
      <FAQ items={pricingFaq} title="Veelgestelde vragen over prijzen" badge="Prijzen" />
      <CalculatorWidget />
      <Contact />
    </>
  );
}
